import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import axiosManager from "../services/axiosManager";
import { isContactPrimary } from "../pages/applicant/ResponsibleAdults/constants";
import { useTranslate } from "./useTranslate";
import { getPrimaryAddress, getPrimaryPhone } from "../utils/getPrimaryData";

export const useGetStudentContacts = (studentId) => {
    const { t } = useTranslate();
    const {
        isLoading, data: contacts, error,
        refetch, isRefetching,
    } = useQuery({
        queryKey: [`student/${studentId}/contacts`],
        queryFn: () => axiosManager.get$(`/parent/api/students/${studentId}/contacts`).then((response) => response?.data),
        retry: 0,
        enabled: !!studentId,
        select: (data) => (data || [])
            .map((item) => ({
                ...item,
                displayName: `${item.title ? `${item.title} ` : ""}${item.forename} ${item.middleNames ? `${item.middleNames} ` : ""}${item.surname}`,
                isPrimary: isContactPrimary(item.contactType),
                primaryAddress: getPrimaryAddress(item.addresses),
                primaryPhone: getPrimaryPhone(item.telephoneNumbers),
            }))
            .toSorted((a, b) => {
                if (isContactPrimary(a.contactType) && !isContactPrimary(b.contactType)) return -1;
                if (!isContactPrimary(a.contactType) && isContactPrimary(b.contactType)) return 1;
                return 0;
            })
            .map((item, idx) => ({ ...item, isPrimary: idx === 0 })),
    });

    const contactsOptions = useMemo(() => (contacts || []).map((contact) => ({
        value: contact.contactId,
        label: `${contact.title ? `${contact.title} ` : ""}${contact.forename} ${contact.surname}`,
    })), [contacts]);

    const contactsAddressOptions = useMemo(() => {
        const baseOptions = (contacts || [])
            .map((contact) => ({
                value: getPrimaryAddress(contact.addresses)?.addressId,
                label: `${contact.title && `${contact.title} `}${contact.forename} ${contact.surname}`,
            }))
            .filter((item) => item.value);

        return [
            ...baseOptions,
            { value: "new", label: t("studentDetails.form.addNew"), icon: <AddIcon /> },
        ];
    }, [contacts, t]);

    return {
        isLoading,
        contacts: contacts || [],
        contactsOptions,
        contactsAddressOptions,
        error,
        refetch,
        isRefetching,
    };
};
